<template>
  <div>
    <div class="w-100 d-flex flex justify-content-between py-4 px-3" style="border: #dce2ed solid 1px;">
      <div class="flex-grow-1">
        <button type="button" @click="expandLogs = !expandLogs" class="bg-transparent border-0 mr-2 font-weight-bold"
                style="outline: none;">
          <i class="fas mr-3" :class="iconClass"></i>
          {{ formatDate(log.date) }}
        </button>
      </div>
      <div class="font-weight-bold text-grey-03">{{ log.logs.length }} Logs</div>
    </div>

    <div class="w-100 py-2 px-4" style="border: #dce2ed solid 1px;" v-if="expandLogs">
      <div class="row my-3" v-for="(activity, key) of log.logs">
        <div class="row w-100">
          <div class="col col-auto d-flex align-items-center">
            <i class="fas" style="color: #8997aa;" :class="getIcon(activity.type, activity.status)"></i>
          </div>
          <div class="col d-flex align-items-center">
            {{ getLogText(activity.type, activity.status) }}
            <span class="text-muted ml-3 mr-3">{{ formatTime(activity.timestamp) }}</span>
            {{ getTriggers(activity) }}
          </div>
          <div class="col-auto" v-if="hasPreview(activity)">
            <button type="button" class="wfh-btn-link-primary" @click="togglePreview(key)">
              {{ showScreenshots === key ? 'Hide' : 'Preview' }}
            </button>
          </div>
        </div>

        <div class="d-flex py-3 screenshot-container" v-if="showScreenshots === key && hasPreview(activity)">
          <div class="screenshot-frame" v-for="screenshot of activity.screenshots"
               :style="screenshotStyle(screenshot)">
            <div class="clickable" @click="toggleLargePreview(screenshot)"></div>
            <button
                v-if="!isAdmin && screenshot.confirmed_at === null && requiresScreenshots"
                type="button"
                class="delete-button"
                @click="addScreenshotsToDelete(key, screenshot)">
              <i class="fas fa-times" style="color: #fff;"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <default-modal v-if="showLargePreview && imageUrl !== null" @close="toggleLargePreview">
      <img :src="imageUrl" alt="Screenshot preview" style="width: 100%;height: 100%;">
    </default-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';

const TEXTS = {
  logs: {
    check_in: 'Check in',
    check_out: 'Check out',
    away: 'Went away',
    break: 'On break',
    continue: 'Continue from break/away',
  },
  tasks: {
    started: 'Start task on',
    paused: 'Pause task on',
  },
};

const ICONS = {
  logs: {
    check_in: 'fa-sign-in-alt',
    check_out: 'fa-sign-out-alt',
    away: 'fa-running',
    break: 'fa-pause-circle',
    continue: 'fa-play-circle',
  },
  tasks: {
    started: 'fa-play',
    paused: 'fa-pause',
  },
};

export default {
  props: {
    log: {
      required: true,
    },
  },

  data () {
    return {
      expandLogs: false,
      showScreenshots: false,
      showLargePreview: false,
      imageUrl: null,
    };
  },

  computed: {
    iconClass () {
      return {
        'fa-chevron-down': this.expandLogs === false,
        'fa-chevron-up': this.expandLogs === true,
      };
    },

    isAdmin () {
      return this.$store.state.isAdmin;
    },

    requiresScreenshots () {
      return this.currentUser.requires_screenshot ?? false;
    },

    ...mapState(['currentUser']),
  },

  methods: {
    formatTime (timestamp) {
      return moment(timestamp).format('hh:mm A');
    },

    formatDate (date) {
      return moment(date).format('DD MMMM yyy');
    },

    getLogText (type, status) {
      return `${TEXTS[type][status]}`;
    },

    getTriggers (activity) {
      return activity.hasOwnProperty('system_triggered') ? (
          activity.system_triggered ? 'system automated' : ''
      ) : '';
    },

    getIcon (type, status) {
      return {
        [ICONS[type][status]]: true,
      };
    },

    togglePreview (key) {
      this.showScreenshots = this.showScreenshots === key ? null : key;
    },

    screenshotStyle (screenshot) {
      return {
        background: `url(${screenshot.url})`,
      };
    },

    toggleLargePreview (screenshot = null) {
      if (screenshot) {
        this.showLargePreview = !this.showLargePreview;
        this.imageUrl = screenshot.url;
      } else {
        this.imageUrl = null;
        this.showLargePreview = false;
      }
    },

    hasPreview (activity) {
      return activity.type === 'tasks' && activity.status === 'started'
          && activity.hasOwnProperty('total_screenshots') && activity.total_screenshots > 0;
    },

    addScreenshotsToDelete (logKey, screenshot) {
      this.$emit('deleteScreenshot', logKey, screenshot);
    },
  },
};
</script>

<style lang="scss" scoped>
.screenshot-frame {
  position: relative;
  margin-right: 8px;
  width: 130px !important;
  height: 130px !important;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  flex: none;
}

.clickable {
  cursor: pointer;
  position: absolute;
  height: 100px;
  width: 130px;
  top: 30px;
}

.delete-button {
  position: absolute;
  right: -7px;
  top: -7px;
  width: 21px;
  height: 21px;
  border-radius: 50%;
  background: #000;
  outline: none;
  border: none;
  line-height: 1.1;
  z-index: 9;
}

.screenshot-container {
  overflow-y: auto;
}
</style>
